import { Form, notification } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useStore } from 'store/useStore'
import { OrderKeys } from 'sections/LKSection/modules/OrderModule/data'
import { Margin } from 'components/ui'
import { Button, Input, Select, Text, TextArea, Title } from 'components/uiKit'
import { getLkGeneralKey, getSharedKey } from 'utils/helpers/i18n/getKeys'
import { WEB_LINK_REGEXP } from 'shared/constants/regExp'

import { CCTVModal } from './CCTVModal/CCTVModal'
import { validateOrderCCTV } from './validate'

type FormType = {
  url: string
  login: string
  password: string
  token: string
  description: string
}

const maxLength = 500

const getKey = (key: string) => `${OrderKeys.activateOrderPage}.${key}`

export const OrderCCTVForm = observer(() => {
  const { activateOrderUiStore: uiStore } = useStore()

  const [form] = Form.useForm()
  const { t } = useTranslation()

  const [loading, setLoading] = useState(false)

  useEffect(() => {
    uiStore.cctv.getVendorList()

    if (uiStore.cctv.initializedCCTVInfo) {
      form.setFieldsValue({
        url: uiStore.cctv.url,
        login: uiStore.cctv.login,
        description: uiStore.cctv.description,
        password: uiStore.cctv.password,
        token: uiStore.cctv.token,
      })
    }
  }, [uiStore.cctv.initializedCCTVInfo, form, uiStore.cctv])

  const nextStepHandler = async () => {
    if (validateOrderCCTV.validate(uiStore.cctv)) {
      const pvzId = uiStore.pvzId
      const orderId = uiStore.orderId

      if (pvzId && orderId) {
        try {
          setLoading(true)

          await uiStore.cctv.addCCTVInfo(orderId)
          const success = await uiStore.cctv.validateCCTVInfo(pvzId)

          if (success) {
            uiStore.setSelectedStep(uiStore.selectedStep + 1)
          } else {
            uiStore.cctv.setShowCCTVModal(true)
          }
        } catch (error) {
          notification.error({ message: (error as Error).message })
        } finally {
          setLoading(false)
        }
      } else {
        notification.error({ message: t(getKey('validate.invalidID')) })
      }
    }
  }

  return (
    <>
      <Form layout="vertical" form={form} requiredMark={false}>
        <Form.Item>
          <Title level={5}>{t(getSharedKey('Видеонаблюдение'))}</Title>
        </Form.Item>

        <Form.Item>
          <Text>{t(getKey('Внесите данные для подключения'))}</Text>
        </Form.Item>

        <Form.Item>
          <Text>
            {'ID ' + t(getLkGeneralKey('ПВЗ'))} {uiStore.pvzId}
          </Text>
        </Form.Item>

        <Form.Item label={t(getKey('Видеосервис'))}>
          <Select
            value={uiStore.cctv.vendorValue}
            onChange={uiStore.cctv.setVendorValue}
            options={uiStore.cctv.vendorList.map((vendor) => ({ value: vendor.id, label: vendor.name }))}
            placeholder={t(getKey('Выберите поставщика сервиса'))}
          />
        </Form.Item>

        <Form.Item<FormType>
          label={t(getKey('Ссылка на сервис'))}
          name={'url'}
          rules={[
            { required: true, message: t(getSharedKey('required')) },
            { pattern: WEB_LINK_REGEXP, message: t(getSharedKey('URLIncorrectMessage')) },
          ]}
          tooltip={t(getKey('Ссылка на web-сервис'))}
        >
          <Input
            placeholder="https://cameras.me/&kxsakj87_jujyhgfasd__XsG4"
            value={uiStore.cctv.url}
            onChange={(e) => uiStore.cctv.setUrl(e.target.value)}
            maxLength={500}
          />
        </Form.Item>

        <Form.Item<FormType>
          label={t(getSharedKey('login'))}
          name={'login'}
          rules={[{ required: true, message: t(getSharedKey('required')) }]}
        >
          <Input
            value={uiStore.cctv.login}
            onChange={(e) => uiStore.cctv.setLogin(e.target.value)}
            placeholder={t(getKey('Логин для входа'))}
            maxLength={maxLength}
          />
        </Form.Item>

        <Form.Item<FormType>
          label={t(getSharedKey('password'))}
          name={'password'}
          rules={[{ required: true, message: t(getSharedKey('required')) }]}
        >
          <Input
            placeholder={t(getKey('Пароль для входа'))}
            value={uiStore.cctv.password}
            maxLength={maxLength}
            onChange={(e) => uiStore.cctv.setPassword(e.target.value)}
          />
        </Form.Item>

        {uiStore.cctv.hasToken && (
          <Form.Item<FormType>
            label={t(getKey('Токен'))}
            name={'token'}
            rules={[{ required: true, message: t(getSharedKey('required')) }]}
          >
            <Input
              value={uiStore.cctv.token}
              onChange={(e) => uiStore.cctv.setToken(e.target.value)}
              maxLength={maxLength * 2}
              placeholder="ksajdhLKJjbUyhnKJh087uubnht5687ylk8098LKJkljlkmk8"
            />
          </Form.Item>
        )}

        <Form.Item<FormType> label={t(getSharedKey('description'))} name={'description'}>
          <TextArea
            value={uiStore.cctv.description}
            onChange={(e) => uiStore.cctv.setDescription(e.target.value)}
            maxLength={maxLength}
            placeholder={t(getKey('Дополнительная информация для входа'))}
          />
        </Form.Item>

        <Margin mt={24}>
          <Button onClick={nextStepHandler} loading={loading}>
            {t(getSharedKey('next'))}
          </Button>
        </Margin>
      </Form>

      {uiStore.cctv.validateCCTVResult && (
        <CCTVModal
          cameras={uiStore.cctv.validateCCTVResult!.cameras}
          isModalOpen={uiStore.cctv.showCCTVModal}
          setIsModalOpen={uiStore.cctv.setShowCCTVModal}
        />
      )}
    </>
  )
})
